/**
 * textures.ts — Surface patterns painted onto clay.
 *
 * The town's walls and roofs learned this trick first (see `masonry.ts`): a
 * repeating pattern belongs on the *surface* of a thing, drawn onto a small
 * canvas, not built out of little meshes standing on it. Boxes only show up if
 * they stand off the surface, so they read as grit rather than texture, and
 * they cost thousands of triangles to say what 256 pixels says better.
 *
 * The ground had never had that treatment, and it showed. Roads, footways,
 * parks and yards are between them the **largest thing on screen** — far larger
 * than any roof — and every one of them was a single flat fill of colour sitting
 * next to a coursed brick wall. This module is the machinery for fixing that,
 * plus the patterns that aren't masonry:
 *
 *   grass    mown stripes with a tuft texture over them
 *   tarmac   fine aggregate speckle
 *   bark     vertical ridges, for tree trunks
 *
 * `masonry.ts` keeps the running-bond patterns (brick, tile, paving) and the
 * UV painting that wraps them round a building, and takes its cache and its
 * material from here so there is only ever one of each.
 *
 * ## The two rules a pattern has to obey
 *
 * 1. **It must tile seamlessly.** Everything drawn here wraps: anything crossing
 *    an edge is drawn again on the opposite one. Miss that and every repeat
 *    shows a seam, which on a road running the length of the map is a stripe
 *    down the middle of the town.
 * 2. **It must land at a fixed world size.** UV space is normalised per face, so
 *    the same texture on a 960-long road and a 48-wide footway would come out at
 *    two different scales. The repeat count is baked into each mesh's own UVs
 *    (`paintPlane` here, `paintBox` in masonry.ts) rather than set on the
 *    texture, because a texture is shared between meshes and its repeat is not
 *    per-mesh.
 *
 * Textures and materials are cached per colour and shared. Never dispose one.
 */
import * as THREE from 'three';
import { CLAY } from '../config/constants';
import { applyBoundsGreyscale } from './clay';
import { hash2 } from '../utils/math';
import { shadeHex } from '../utils/color';

/** A pattern's colour map and the matching relief, generated together. */
export interface SurfaceTexture {
  map: THREE.CanvasTexture;
  bump: THREE.CanvasTexture;
}

const texCache = new Map<string, SurfaceTexture>();
const matCache = new Map<string, THREE.MeshStandardMaterial>();

/**
 * Build (or fetch) a cached texture pair from a draw function.
 *
 * `draw(relief)` is called twice: once for colour and once for the bump map,
 * where it should draw the *same shapes* in greys. That second pass is what
 * stops a pattern looking printed on — it gives every unit an edge for the light
 * to catch, which is the whole reason the surface still reads as pressed clay
 * now that the detail isn't geometry.
 */
export function patternTexture(
  key: string,
  draw: (relief: boolean) => HTMLCanvasElement,
): SurfaceTexture {
  const hit = texCache.get(key);
  if (hit) return hit;

  const made = { map: canvasTexture(draw(false)), bump: canvasTexture(draw(true)) };
  texCache.set(key, made);
  return made;
}

function canvasTexture(canvas: HTMLCanvasElement): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(canvas);
  // The UVs run well past 1 — that's how the pattern reaches its world size.
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  // Ground and roofs are both seen at a steep glancing angle from this camera,
  // which is exactly where an unfiltered repeating pattern turns into moiré.
  tex.anisotropy = 8;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * A matte clay material carrying a pattern.
 *
 * Same recipe as `clayMaterial` — see the note there about the faint self-glow —
 * with the pattern as both colour and relief. Cached alongside the texture, so
 * every surface of the same kind shares one material and one draw call's worth
 * of state.
 */
export function patternedClay(
  color: number,
  texture: SurfaceTexture,
  bumpScale: number,
): THREE.MeshStandardMaterial {
  const key = `${color}:${texture.map.id}`;
  const hit = matCache.get(key);
  if (hit) return hit;

  const mat = new THREE.MeshStandardMaterial({
    color: 0xffffff, // the pattern already carries the surface's colour
    map: texture.map,
    bumpMap: texture.bump,
    bumpScale,
    roughness: CLAY.ROUGHNESS,
    metalness: 0,
    emissive: color,
    emissiveIntensity: CLAY.SUBSURFACE,
    dithering: true,
  });
  applyBoundsGreyscale(mat);
  matCache.set(key, mat);
  return mat;
}

/** A blank canvas of `px` square, filled with `fill`. */
function sheet(px: number, fill: string): [HTMLCanvasElement, CanvasRenderingContext2D | null] {
  const canvas = document.createElement('canvas');
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = fill;
    ctx.fillRect(0, 0, px, px);
  }
  return [canvas, ctx];
}

export const hex = (color: number): string => `#${color.toString(16).padStart(6, '0')}`;

/** Resolution of a ground pattern's tile. Ground is seen closer than a roof. */
const GROUND_PX = 256;

/**
 * Grass: mown stripes, with a scatter of tufts over them.
 *
 * The stripes are the important half. A park is a big flat rectangle of one
 * green, and from overhead that reads as a painted panel however finely it's
 * speckled — what says "lawn" is the alternating light and dark bands a mower
 * leaves, which is a large-scale feature the eye picks up instantly at this
 * distance. The tufts then stop the bands themselves being flat.
 *
 * `bands` must divide the tile evenly or the stripes seam where it wraps.
 */
export function grassTexture(color: number, bands = 4): SurfaceTexture {
  return patternTexture(`grass:${color}:${bands}`, (relief) => {
    const [canvas, ctx] = sheet(GROUND_PX, relief ? '#808080' : hex(color));
    if (!ctx) return canvas;

    // Mown bands, alternating with the cut direction.
    const bandPx = GROUND_PX / bands;
    for (let i = 0; i < bands; i += 2) {
      ctx.fillStyle = relief ? '#8c8c8c' : hex(shadeHex(color, 0.12));
      ctx.fillRect(0, i * bandPx, GROUND_PX, bandPx);
    }

    // Tufts: short strokes, angled with the band they sit in so the mowing
    // direction reads in the grain as well as the tone.
    //
    // Fine and low-contrast on purpose. One tile covers GRASS_CELL world units,
    // so a stroke a few pixels long is a couple of units of grass — push either
    // the length or the tonal step and the lawn stops reading as grass and
    // starts reading as litter scattered over it, which is exactly what the
    // first attempt looked like.
    const tufts = 2200;
    ctx.lineWidth = 1;
    for (let i = 0; i < tufts; i++) {
      const x = hash2(i * 1.7, 11.3) * GROUND_PX;
      const y = hash2(i * 2.3, 5.1) * GROUND_PX;
      const band = Math.floor(y / bandPx);
      const lean = band % 2 === 0 ? 1 : -1;
      const len = 2 + hash2(i, 31.7) * 2.5;
      const lighter = hash2(i * 3.1, 17.9) > 0.5;
      ctx.strokeStyle = relief
        ? lighter
          ? '#a8a8a8'
          : '#585858'
        : hex(shadeHex(color, lighter ? 0.09 : -0.08));
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + lean * len * 0.4, y - len);
      ctx.stroke();
      // Wrap: a stroke crossing the top edge is drawn again at the bottom.
      if (y - len < 0) {
        ctx.beginPath();
        ctx.moveTo(x, y + GROUND_PX);
        ctx.lineTo(x + lean * len * 0.4, y + GROUND_PX - len);
        ctx.stroke();
      }
    }
    return canvas;
  });
}

/**
 * Tarmac: fine aggregate, and nothing else.
 *
 * Deliberately the quietest pattern in the game. The road is the widest single
 * surface in the town and it runs the whole length of it, so anything with a
 * *shape* to it — a bond, a stripe, a repeat you can name — turns into a visible
 * tiling artefact the moment two spans meet. Speckle has no shape to notice, and
 * all it has to do is stop the tarmac being a flat fill next to a brick wall.
 */
export function tarmacTexture(color: number): SurfaceTexture {
  return patternTexture(`tarmac:${color}`, (relief) => {
    const [canvas, ctx] = sheet(GROUND_PX, relief ? '#808080' : hex(color));
    if (!ctx) return canvas;

    for (let i = 0; i < 2600; i++) {
      const x = hash2(i * 1.1, 7.7) * GROUND_PX;
      const y = hash2(i * 1.9, 3.3) * GROUND_PX;
      const r = 0.6 + hash2(i, 13.1) * 1.5;
      const lighter = hash2(i * 2.7, 23.3) > 0.45;
      ctx.fillStyle = relief
        ? lighter
          ? '#c8c8c8'
          : '#4a4a4a'
        : hex(shadeHex(color, lighter ? 0.14 : -0.1));
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    return canvas;
  });
}

/**
 * Scrub: rough, unmown ground for the block interiors.
 *
 * The opposite of `grassTexture` in the one way that counts — **no bands.**
 * Mown stripes are what say "someone cuts this", so a park gets them and a back
 * garden must not, or the two read as the same surface in two colours. What
 * says "nobody cuts this" is scale and disorder: tussocks several times longer
 * than a park tuft, thrown at every angle, with bare scuffs showing through
 * where the ground has been walked over.
 *
 * The scuffs go down first and the growth over them, so the bare patches read
 * as worn *through* the grass rather than as pale blotches painted on it.
 */
export function scrubTexture(color: number, soil: number): SurfaceTexture {
  return patternTexture(`scrub:${color}:${soil}`, (relief) => {
    const [canvas, ctx] = sheet(GROUND_PX, relief ? '#808080' : hex(color));
    if (!ctx) return canvas;

    // Worn ground showing through: soft irregular patches of bare soil.
    for (let i = 0; i < 14; i++) {
      const x = hash2(i * 4.1, 2.9) * GROUND_PX;
      const y = hash2(i * 6.7, 8.3) * GROUND_PX;
      const r = 6 + hash2(i, 41.3) * 13;
      ctx.fillStyle = relief ? '#767676' : hex(soil);
      for (const [ox, oy] of wraps(x, y, r)) {
        ctx.beginPath();
        ctx.ellipse(ox, oy, r, r * (0.5 + hash2(i, 3.7) * 0.6), hash2(i, 5.5) * Math.PI, 0, 7);
        ctx.fill();
      }
    }

    // Tussocks. Long, and thrown at any angle — a park tuft is 2–5px and leans
    // with its band; these are 5–14 and lean anywhere, which at this cell size
    // is knee-high grass rather than a cut lawn.
    ctx.lineWidth = 1.4;
    for (let i = 0; i < 1500; i++) {
      const x = hash2(i * 1.3, 19.7) * GROUND_PX;
      const y = hash2(i * 2.9, 6.1) * GROUND_PX;
      const angle = hash2(i * 3.7, 11.9) * Math.PI * 2;
      const len = 5 + hash2(i, 29.3) * 9;
      const lighter = hash2(i * 5.3, 2.1) > 0.45;
      ctx.strokeStyle = relief
        ? lighter
          ? '#b4b4b4'
          : '#4c4c4c'
        : hex(shadeHex(color, lighter ? 0.16 : -0.14));
      const dx = Math.cos(angle) * len * 0.45;
      const dy = -Math.abs(Math.sin(angle)) * len;
      for (const [ox, oy] of wraps(x, y, len)) {
        ctx.beginPath();
        ctx.moveTo(ox, oy);
        ctx.lineTo(ox + dx, oy + dy);
        ctx.stroke();
      }
    }
    return canvas;
  });
}

/**
 * Every copy of a mark at (x, y) needed for the tile to wrap seamlessly.
 *
 * Anything within `r` of an edge is drawn again on the opposite side. Without
 * it a tussock crossing the top of the tile is simply cut in half, and since a
 * scrub patch repeats several times across a block, that cut becomes a visible
 * line ruled across the garden.
 */
function wraps(x: number, y: number, r: number): [number, number][] {
  const xs = [x];
  const ys = [y];
  if (x - r < 0) xs.push(x + GROUND_PX);
  if (x + r > GROUND_PX) xs.push(x - GROUND_PX);
  if (y - r < 0) ys.push(y + GROUND_PX);
  if (y + r > GROUND_PX) ys.push(y - GROUND_PX);
  return xs.flatMap((ox) => ys.map((oy): [number, number] => [ox, oy]));
}

/**
 * Bark: vertical ridges down a trunk.
 *
 * Trunks are the one place in the town where the pattern should run *up* rather
 * than around, so this one is drawn as columns. It's cheap detail on an object
 * the player walks past constantly — a tree is the most repeated prop in the
 * game after the windows, and a smooth trunk beside a coursed brick wall was the
 * clearest remaining tell that the scenery hadn't had the same care as the
 * houses.
 */
export function barkTexture(color: number): SurfaceTexture {
  return patternTexture(`bark:${color}`, (relief) => {
    const px = 128;
    const [canvas, ctx] = sheet(px, relief ? '#808080' : hex(color));
    if (!ctx) return canvas;

    const ridges = 10;
    for (let i = 0; i < ridges; i++) {
      const x = (i / ridges) * px + hash2(i, 2.5) * 4;
      const w = 3 + hash2(i, 9.1) * 5;
      const lighter = i % 2 === 0;
      ctx.fillStyle = relief
        ? lighter
          ? '#e0e0e0'
          : '#404040'
        : hex(shadeHex(color, lighter ? 0.16 : -0.18));
      // Ridges wander a little rather than running dead straight, or the trunk
      // reads as a bundle of pipes.
      ctx.beginPath();
      ctx.moveTo(x, 0);
      for (let y = 0; y <= px; y += 16) {
        ctx.lineTo(x + (hash2(i * 3.3, y * 0.7) - 0.5) * 5, y);
      }
      for (let y = px; y >= 0; y -= 16) {
        ctx.lineTo(x + w + (hash2(i * 5.1, y * 0.9) - 0.5) * 5, y);
      }
      ctx.closePath();
      ctx.fill();
    }
    return canvas;
  });
}

/**
 * Rescale a plane's UVs so its pattern tiles at a fixed world size.
 *
 * The flat-ground counterpart to `masonry.ts` → `paintBox`. A `PlaneGeometry`
 * runs u and v from 0 to 1 whatever its size, so this multiplies them up by the
 * real extent divided by the pattern's world cell. Counts are **rounded to whole
 * numbers**, for the same reason the walls' are: a fractional repeat cuts the
 * pattern mid-unit where it wraps, and on the ground that seam runs the length
 * of the street.
 *
 * The geometry must be unshared — `claySlab` builds a fresh plane per call, so
 * it is.
 */
export function paintPlane(
  geo: THREE.BufferGeometry,
  w: number,
  d: number,
  cell: number,
): void {
  const uv = geo.getAttribute('uv');
  if (!uv) return;
  const u = Math.max(1, Math.round(w / cell));
  const v = Math.max(1, Math.round(d / cell));
  for (let i = 0; i < uv.count; i++) {
    uv.setXY(i, uv.getX(i) * u, uv.getY(i) * v);
  }
  uv.needsUpdate = true;
}
